import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SquareArrowOutUpRight } from 'lucide-react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

export const CLR_EXPORT_REPORT_CONFIG = {
  ecm_rt: { label: 'ECM Provider Return Transmission', freq: 'monthly' },
  cs_rt: { label: 'CS Provider Return Transmission', freq: 'monthly' },
  ecm_cap: { label: 'ECM Quarterly Capacity', freq: 'quarterly' },
  cs_cap: { label: 'CS Quarterly Capacity', freq: 'quarterly' },
  ecm_pmr: { label: 'ECM Potential Member Referral', freq: 'quarterly' },
  ecm_iot: { label: 'ECM Provider Initial Outreach Tracker', freq: 'quarterly' },
};

const REPORT_OPTIONS = Object.entries(CLR_EXPORT_REPORT_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
}));

function ChevronIcon() {
  return (
    <svg className="clr-icon" width="18" height="18" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M1 1.5L6 6.5L11 1.5"
        stroke="#5F7470"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const PeriodTrigger = forwardRef(({ value, label, placeholder = 'Month/Quarter', disabled, onClick }, ref) => {
  // Prefer `label` — react-datepicker clones customInput and overrides `value`.
  const text = (label != null && String(label).trim() !== '')
    ? String(label)
    : (value != null && String(value).trim() !== '' ? String(value) : '');
  const showPlaceholder = !text;

  return (
    <button
      type="button"
      ref={ref}
      className={`clr-export-dialog__select-trigger${showPlaceholder ? ' is-placeholder' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="clr-export-dialog__select-label">
        {showPlaceholder ? placeholder : text}
      </span>
      <span className="clr-export-dialog__select-icon">
        <ChevronIcon />
      </span>
    </button>
  );
});

PeriodTrigger.displayName = 'PeriodTrigger';
PeriodTrigger.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

function ClrExportDialog({ open, onClose, onExportOtf, onExportMif, exporting = false }) {
  const [selectedReport, setSelectedReport] = useState(null);
  const [exportDate, setExportDate] = useState(null);
  const [periodPickerOpen, setPeriodPickerOpen] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const reportFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setSelectedReport(null);
    setExportDate(null);
    setPeriodPickerOpen(false);
    setReportMenuOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (reportMenuOpen) {
        setReportMenuOpen(false);
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, reportMenuOpen]);

  useEffect(() => {
    if (!reportMenuOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (!reportFieldRef.current?.contains(event.target)) {
        setReportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [reportMenuOpen]);

  const reportConfig = selectedReport ? CLR_EXPORT_REPORT_CONFIG[selectedReport] : null;
  const isMonthly = reportConfig?.freq !== 'quarterly';
  const selectedReportLabel = reportConfig?.label || '';
  const periodLabel = exportDate
    ? format(exportDate, isMonthly ? 'MMM yyyy' : 'QQQ yyyy')
    : '';

  const exportPayload = useMemo(() => ({
    reportType: selectedReport,
    exportDate,
    isMonthly,
  }), [selectedReport, exportDate, isMonthly]);

  const canExport = Boolean(selectedReport && exportDate) && !exporting;

  const handleReportSelect = (nextValue) => {
    setSelectedReport(nextValue);
    setReportMenuOpen(false);
    setPeriodPickerOpen(false);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="clr-export-dialog-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="clr-export-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="clr-export-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="clr-export-dialog__header">
          <h2 id="clr-export-dialog-title" className="clr-export-dialog__title">
            Export Options
          </h2>
          <button
            type="button"
            className="clr-export-dialog__close"
            aria-label="Close export dialog"
            onClick={onClose}
          >
            <svg className="clr-icon" width="18" height="18" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M14 8L8 14M8 8L14 14M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
                stroke="#293C38"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="clr-export-dialog__body">
          <div className="clr-export-dialog__fields">
            <div
              className={`clr-export-dialog__field clr-export-dialog__report-field${reportMenuOpen ? ' is-open' : ''}`}
              ref={reportFieldRef}
            >
              <span className="clr-export-dialog__label" id="clr-export-report-label">
                Select report type
              </span>
              <button
                type="button"
                className={`clr-export-dialog__select-trigger${selectedReport ? '' : ' is-placeholder'}`}
                aria-haspopup="listbox"
                aria-expanded={reportMenuOpen}
                aria-labelledby="clr-export-report-label"
                disabled={exporting}
                onClick={() => {
                  setReportMenuOpen((prev) => !prev);
                  setPeriodPickerOpen(false);
                }}
              >
                <span className="clr-export-dialog__select-label">
                  {selectedReportLabel || 'Report type'}
                </span>
                <span className="clr-export-dialog__select-icon">
                  <ChevronIcon />
                </span>
              </button>
              {reportMenuOpen && (
                <div className="clr-export-dialog__select-menu" role="listbox">
                  {REPORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={option.value === selectedReport}
                      className={`clr-export-dialog__select-option${option.value === selectedReport ? ' is-selected' : ''}`}
                      onClick={() => handleReportSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={`clr-export-dialog__field clr-export-dialog__period-field${periodPickerOpen ? ' is-open' : ''}`}>
              <span className="clr-export-dialog__label" id="clr-export-period-label">
                Select month or quarter
              </span>
              <DatePicker
                selected={exportDate}
                open={periodPickerOpen}
                onInputClick={() => {
                  setPeriodPickerOpen(true);
                  setReportMenuOpen(false);
                }}
                onClickOutside={() => setPeriodPickerOpen(false)}
                onCalendarClose={() => setPeriodPickerOpen(false)}
                onSelect={(date) => {
                  if (!date) return;
                  setExportDate(date);
                  window.setTimeout(() => setPeriodPickerOpen(false), 0);
                }}
                onChange={(date) => {
                  if (!date) return;
                  setExportDate(date);
                  window.setTimeout(() => setPeriodPickerOpen(false), 0);
                }}
                shouldCloseOnSelect
                showMonthYearPicker={isMonthly}
                showQuarterYearPicker={!isMonthly}
                dateFormat={isMonthly ? 'MMM yyyy' : 'QQQ yyyy'}
                customInput={(
                  <PeriodTrigger
                    label={periodLabel}
                    placeholder="Month/Quarter"
                    disabled={exporting}
                  />
                )}
                placeholderText="Month/Quarter"
                calendarClassName="clr-export-dialog__calendar"
                popperClassName="clr-export-dialog__popper"
                popperPlacement="bottom-start"
                disabled={exporting}
                ariaLabelledBy="clr-export-period-label"
              />
            </div>
          </div>

          <p className="clr-export-dialog__message">
            Choose how you&apos;d like to export your document and continue with the appropriate format.
          </p>
        </div>

        <div className="clr-export-dialog__actions">
          <button
            type="button"
            className="clr-export-dialog__btn clr-export-dialog__btn--mif"
            onClick={() => onExportMif(exportPayload)}
            disabled={!canExport}
          >
            <SquareArrowOutUpRight className="clr-icon clr-export-dialog__btn-icon" size={18} aria-hidden="true" />
            Export MIF
          </button>
          <button
            type="button"
            className="clr-export-dialog__btn clr-export-dialog__btn--otf"
            onClick={() => onExportOtf(exportPayload)}
            disabled={!canExport}
          >
            <SquareArrowOutUpRight className="clr-icon clr-export-dialog__btn-icon" size={18} aria-hidden="true" />
            Export OTF
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

ClrExportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExportOtf: PropTypes.func.isRequired,
  onExportMif: PropTypes.func.isRequired,
  exporting: PropTypes.bool,
};

export default ClrExportDialog;
